import { useCallback, useState } from "react";

import * as api from "../api/client";
import { MetricCard } from "../components/MetricCard";
import { SortableTh } from "../components/SortableTh";
import { useApi } from "../hooks/useApi";
import { useSortableRows } from "../hooks/useSortableRows";
import type { RecentVisit } from "../types/api";

const WINDOW_OPTIONS = [7, 30, 90];
const RECENT_VISITS_LIMIT = 50;

const TD = "px-4 py-2.5 text-sm text-slate-900";

type SourceSortKey = "label" | "count" | "valueSum";
const SOURCE_ACCESSORS: Record<SourceSortKey, (r: SourceRow) => string | number | null> = {
  label: (r) => r.label,
  count: (r) => r.count,
  valueSum: (r) => r.valueSum ?? null,
};

type VisitSortKey =
  | "visitToken"
  | "createdAt"
  | "utmSource"
  | "utmMedium"
  | "utmCampaign"
  | "hasFbclid"
  | "hasTtclid"
  | "hasGclid"
  | "welcomeAt"
  | "landingUrl";
const VISIT_ACCESSORS: Record<VisitSortKey, (v: RecentVisit) => string | number | null> = {
  visitToken: (v) => v.visitToken,
  createdAt: (v) => v.createdAt?.getTime() ?? null,
  utmSource: (v) => v.utmSource ?? null,
  utmMedium: (v) => v.utmMedium ?? null,
  utmCampaign: (v) => v.utmCampaign ?? null,
  hasFbclid: (v) => (v.hasFbclid ? 1 : 0),
  hasTtclid: (v) => (v.hasTtclid ? 1 : 0),
  hasGclid: (v) => (v.hasGclid ? 1 : 0),
  welcomeAt: (v) => v.welcomeAt?.getTime() ?? null,
  landingUrl: (v) => v.landingUrl ?? null,
};

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatEur(amount: number): string {
  return `€${amount.toFixed(2)}`;
}

function formatDate(value: Date | null | undefined): string {
  if (!value) return "—";
  return Number.isNaN(value.getTime()) ? "—" : value.toLocaleString();
}

function checkOrDash(value: boolean): string {
  return value ? "✓" : "—";
}

interface SourceRow {
  label: string;
  count: number;
  valueSum?: number;
}

function SourceTable({
  title,
  countLabel,
  rows,
  showValue,
  caption,
}: {
  title: string;
  countLabel: string;
  rows: SourceRow[];
  showValue?: boolean;
  /** One-sentence explainer shown under the title, e.g. for a column whose meaning isn't self-evident. */
  caption?: string;
}) {
  // Default: highest-count source first — useful without clicking (PK 2026-07-25 directive).
  const sourceSort = useSortableRows(rows, SOURCE_ACCESSORS, { key: "count", direction: "desc" });

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h4 className="text-sm font-semibold text-slate-800">{title}</h4>
        {caption && <p className="text-xs text-slate-500 mt-1">{caption}</p>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-slate-200">
            <tr>
              <SortableTh
                label="Source"
                sortKey="label"
                sort={sourceSort.sort}
                onSort={sourceSort.toggleSort}
                sticky
              />
              <SortableTh
                label={countLabel}
                sortKey="count"
                sort={sourceSort.sort}
                onSort={sourceSort.toggleSort}
                align="right"
              />
              {showValue && (
                <SortableTh
                  label="Value (EUR)"
                  sortKey="valueSum"
                  sort={sourceSort.sort}
                  onSort={sourceSort.toggleSort}
                  align="right"
                />
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {sourceSort.sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={showValue ? 3 : 2}
                  className="px-4 py-6 text-center text-sm text-slate-400"
                >
                  No data in this window
                </td>
              </tr>
            ) : (
              sourceSort.sorted.map((row) => (
                <tr key={row.label} className="group hover:bg-slate-50 transition-colors">
                  <td className={`${TD} sticky left-0 z-10 bg-white group-hover:bg-slate-50`}>
                    {row.label}
                  </td>
                  <td className={`${TD} text-right font-mono`}>{row.count.toLocaleString()}</td>
                  {showValue && (
                    <td className={`${TD} text-right font-mono`}>{formatEur(row.valueSum ?? 0)}</td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecentVisitsTable({ visits }: { visits: RecentVisit[] }) {
  // Default: most-recently-created visit first — useful without clicking (PK 2026-07-25 directive).
  const visitSort = useSortableRows(visits, VISIT_ACCESSORS, {
    key: "createdAt",
    direction: "desc",
  });

  if (visits.length === 0) {
    return <div className="text-slate-500 text-sm">No visits recorded.</div>;
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortableTh
                label="Token"
                sortKey="visitToken"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
                sticky
              />
              <SortableTh
                label="Created"
                sortKey="createdAt"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
              />
              <SortableTh
                label="UTM Source"
                sortKey="utmSource"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
              />
              <SortableTh
                label="UTM Medium"
                sortKey="utmMedium"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
              />
              <SortableTh
                label="UTM Campaign"
                sortKey="utmCampaign"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
              />
              <SortableTh
                label="fbclid"
                sortKey="hasFbclid"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
              />
              <SortableTh
                label="ttclid"
                sortKey="hasTtclid"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
              />
              <SortableTh
                label="gclid"
                sortKey="hasGclid"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
              />
              <SortableTh
                label="Welcome"
                sortKey="welcomeAt"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
              />
              <SortableTh
                label="Landing URL"
                sortKey="landingUrl"
                sort={visitSort.sort}
                onSort={visitSort.toggleSort}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {visitSort.sorted.map((v) => (
              <tr key={v.visitToken} className="group hover:bg-slate-50 transition-colors">
                <td
                  className={`${TD} sticky left-0 z-10 bg-white group-hover:bg-slate-50 font-mono text-xs`}
                >
                  {v.visitToken}
                </td>
                <td className="px-4 py-2.5 text-sm text-slate-600">{formatDate(v.createdAt)}</td>
                <td className={TD}>{v.utmSource ?? "—"}</td>
                <td className={TD}>{v.utmMedium ?? "—"}</td>
                <td className={TD}>{v.utmCampaign ?? "—"}</td>
                <td className={TD}>{checkOrDash(v.hasFbclid)}</td>
                <td className={TD}>{checkOrDash(v.hasTtclid)}</td>
                <td className={TD}>{checkOrDash(v.hasGclid)}</td>
                <td className={TD}>{checkOrDash(v.welcomeAt != null)}</td>
                <td
                  className="px-4 py-2.5 text-sm text-slate-600 max-w-[280px] truncate"
                  title={v.landingUrl ?? undefined}
                >
                  {v.landingUrl ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AttributionPage() {
  const [days, setDays] = useState(30);
  const summary = useApi(() => api.getAttributionSummary(days), [days]);
  const visits = useApi(() => api.listRecentVisits(RECENT_VISITS_LIMIT), []);
  const [refreshing, setRefreshing] = useState(false);

  // Destructure the stable refetch callbacks so exhaustive-deps can verify them
  // as direct identifiers rather than property accesses on frequently-recreated objects.
  const refetchSummary = summary.refetch;
  const refetchVisits = visits.refetch;

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refetchSummary(), refetchVisits()]);
    } finally {
      setRefreshing(false);
    }
  }, [refetchSummary, refetchVisits]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Attribution</h2>
          <p className="text-sm text-slate-600">
            Acquisition funnel, sources, and recent visit traffic.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {WINDOW_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  days === d
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing || summary.loading || visits.loading}
            className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {summary.data?.truncated && (
        <div
          className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4"
          role="alert"
        >
          <span className="text-2xl">⚠️</span>
          <div>
            <div className="text-amber-800 font-semibold">Data truncated for this window</div>
            <div className="text-amber-700 text-sm">
              One or more collections hit the read cap — counts below are a lower bound.
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Funnel + sources (window-scoped summary)                           */}
      {/* ------------------------------------------------------------------ */}
      {summary.loading && (
        <div className="text-slate-500 text-sm py-6 text-center">Loading attribution summary…</div>
      )}

      {!summary.loading && summary.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" role="alert">
          Error: {summary.error}
        </div>
      )}

      {summary.data && !summary.loading && (
        <>
          <section className="space-y-3">
            <h3 className="text-lg font-semibold text-slate-900">Funnel</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <MetricCard label="Visits" value={summary.data.funnel.visits.toLocaleString()} />
              <MetricCard
                label="Welcome Pings"
                value={`${summary.data.funnel.visitsWithWelcome.toLocaleString()} (${formatPercent(
                  summary.data.visits.welcomeRate
                )})`}
              />
              <MetricCard
                label="Install (cookie-stitch)"
                value={summary.data.funnel.installStitched.toLocaleString()}
                hint="Lower bound, not a real install count — only installs correlated back to a prior marketing-page visit (consent + Chrome/Edge + welcome tab still open). Do not diff this against the Chrome Web Store developer dashboard's install number; most installs from the store's own search/browse have no prior visit to stitch. See backend attribution_stats_service.py for the full mechanism."
              />
              <MetricCard
                label="Claimed Acquisitions"
                value={summary.data.funnel.claimedAcquisitions.toLocaleString()}
              />
              <MetricCard
                label="Purchases w/ Snapshot"
                value={summary.data.funnel.purchasesWithSnapshot.toLocaleString()}
              />
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold text-slate-900">Sources</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SourceTable
                title={`Users by Acquisition Source (${summary.data.users.total.toLocaleString()} total)`}
                countLabel="Users"
                rows={summary.data.users.bySource.map((b) => ({ label: b.key, count: b.count }))}
              />
              <SourceTable
                title={`Visits by UTM Source (${summary.data.visits.total.toLocaleString()} total)`}
                countLabel="Visits"
                rows={summary.data.visits.byUtmSource.map((b) => ({
                  label: b.source,
                  count: b.count,
                }))}
              />
              <SourceTable
                title={`Conversions by Snapshot Source (${summary.data.conversions.withSnapshotCount.toLocaleString()} w/ snapshot, ${summary.data.conversions.withoutSnapshotCount.toLocaleString()} without)`}
                countLabel="Purchases"
                showValue
                rows={summary.data.conversions.bySnapshotSource.map((b) => ({
                  label: b.source,
                  count: b.count,
                  valueSum: b.valueSum,
                }))}
              />
            </div>
          </section>

          {/* --------------------------------------------------------------
              Campaign materials: which UTM campaign/content/landing path
              actually brought people in. Fields are optional on the wire
              (see AttributionSummaryResponseWithMaterialBreakdown) so this
              whole section stays hidden against a pre-deployment backend
              instead of crashing or showing three empty tables.
              ------------------------------------------------------------ */}
          {(summary.data.visits.byUtmCampaign !== undefined ||
            summary.data.visits.byUtmContent !== undefined ||
            summary.data.visits.byLandingPath !== undefined) && (
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-slate-900">Campaign Materials</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {summary.data.visits.byUtmCampaign !== undefined && (
                  <SourceTable
                    title="Visits by UTM Campaign"
                    countLabel="Visits"
                    rows={summary.data.visits.byUtmCampaign.map((b) => ({
                      label: b.key,
                      count: b.count,
                    }))}
                  />
                )}
                {summary.data.visits.byUtmContent !== undefined && (
                  <SourceTable
                    title="Visits by UTM Content"
                    countLabel="Visits"
                    rows={summary.data.visits.byUtmContent.map((b) => ({
                      label: b.key,
                      count: b.count,
                    }))}
                  />
                )}
                {summary.data.visits.byLandingPath !== undefined && (
                  <SourceTable
                    title="Visits by Landing Path"
                    countLabel="Visits"
                    caption="Entry path of the visit; promotional materials are tagged with a /p/<campaign-name> path."
                    rows={summary.data.visits.byLandingPath.map((b) => ({
                      label: b.key,
                      count: b.count,
                    }))}
                  />
                )}
              </div>
            </section>
          )}
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Recent visits (fixed limit, not window-scoped)                     */}
      {/* ------------------------------------------------------------------ */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">
          Recent Visits (last {RECENT_VISITS_LIMIT})
        </h3>

        {visits.loading && (
          <div className="text-slate-500 text-sm py-6 text-center">Loading recent visits…</div>
        )}

        {!visits.loading && visits.error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" role="alert">
            Error: {visits.error}
          </div>
        )}

        {visits.data && !visits.loading && <RecentVisitsTable visits={visits.data.visits} />}
      </section>
    </div>
  );
}
